// src/portal-app/tabs/HomeTab.js
//
// Home / Overview surface (Plan 2 Task 7) — restructured onto the shared
// kit built in Tasks 3-6, matching OperationsTab.js / TeamTab.js house
// style (ensureKitCss + ensureTabCss, kit components, useHubCards gating).
//
// This is a RESTRUCTURE, not a redesign: same data source, same greeting +
// "Open" live pill, same station accent colours, same loading/error/empty
// states. What moved onto kit components: the hero revenue card (now
// HeroStat, including its sparkline — the old hand-rolled sparkPaths() SVG
// maths is gone), the low-stock/discrepancy callouts (now AttentionList,
// previously fetched but never rendered), and the on-shift rows (now
// CondensedRow). The on-shift mini-KPI count is also corrected here — see
// the comment above `clockedInNow` below.
//
// Data source — reused UNCHANGED from the web OverviewPage:
// dashboardService.getStats() → GET /portal/dashboard-stats. Same endpoint,
// same params (none); this file adds only presentation state (loading /
// ready / error + retry).
import React, {
    useState, useEffect, useMemo, useCallback,
} from 'react';
import { GlassCard } from '../shell/LiquidGlass';
import { dashboardService, eodReportService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useNav } from '../nav/NavStack';
import { useHubCards } from '../nav/useHubCards';
import {
    HeroStat, AttentionList, CondensedRow, ensureKitCss, toneVar,
} from '../shell/kit';
import { ensureTabCss } from './tabCss';
import HomeEodGlance from './HomeEodGlance';

/* ---- Home-specific stylesheet ----
   Everything a kit component (HeroStat / AttentionList / CondensedRow) or
   the shared tab sheet (tabCss.js — .tab-wrap/.tab-skel/.tab-state/
   .tab-retry) already supplies has been deleted from here. What remains is
   genuinely Home-only: the greeting + live pill (verbatim per the brief),
   the mini-KPI row (no sibling landing has one), the on-shift avatar swatch
   (carries the station accent colour CondensedRow's tone system can't
   express), and the 2x2 quick-action grid — Home is the ONE screen
   permitted a 2x2 grid, and only because these tiles are actions, never
   plain navigation (spec §5 P7). */
const CSS = `
.hmt-greet{padding-inline:16px;padding-block:2px 14px;}
.hmt-hello{margin:0;font-size:22px;font-weight:800;letter-spacing:-.5px;color:var(--c-ink);}
.hmt-biz{margin-block-start:3px;font-size:13px;font-weight:500;color:var(--c-textLight);
  display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.hmt-live{display:inline-flex;align-items:center;gap:5px;
  font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;
  color:var(--c-success);
  background:color-mix(in srgb,var(--c-success) 10%,transparent);
  border:1px solid color-mix(in srgb,var(--c-success) 22%,transparent);
  padding-block:2.5px;padding-inline:9px;border-radius:999px;}
.hmt-pulse{width:6px;height:6px;border-radius:50%;background:var(--c-success);
  box-shadow:0 0 0 0 color-mix(in srgb,var(--c-success) 45%,transparent);
  animation:hmtPulse 2.2s infinite;}
@keyframes hmtPulse{
  0%{box-shadow:0 0 0 0 color-mix(in srgb,var(--c-success) 45%,transparent);}
  70%{box-shadow:0 0 0 6px transparent;}
  100%{box-shadow:0 0 0 0 transparent;}}

.hmt-hero-card{padding:20px 22px 22px;margin-inline:16px;margin-block-end:12px;}

.hmt-kpis{display:flex;gap:10px;margin-inline:16px;margin-block-end:12px;}
.hmt-kpi{flex:1;padding:13px 14px;border-radius:22px;box-sizing:border-box;min-width:0;}
.hmt-kpi-ic{width:30px;height:30px;border-radius:10px;
  display:inline-flex;align-items:center;justify-content:center;
  background:color-mix(in srgb,var(--c-green) 12%,transparent);
  color:var(--c-greenDark);margin-block-end:9px;}
.hmt-kpi-ic.hmt-amber{background:color-mix(in srgb,var(--c-amber) 12%,transparent);color:var(--c-amber);}
.hmt-kpi-ic svg{width:16px;height:16px;}
.hmt-kpi-v{font-size:19px;font-weight:800;letter-spacing:-.5px;color:var(--c-ink);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.hmt-kpi-k{margin-block-start:2px;font-size:10.5px;font-weight:600;letter-spacing:.6px;
  text-transform:uppercase;color:var(--c-textLight);}

.hmt-sechead{display:flex;align-items:center;justify-content:space-between;
  margin-inline:16px;margin-block:18px 8px;}

/* Fills CondensedRow's 32x32 .pk-isq icon slot exactly — the tone passed to
   CondensedRow is neutral ('muted'); this swatch carries the REAL colour
   (STATION_VARS), preserved verbatim from the old design because it is the
   one piece of colour-as-meaning the brief calls out explicitly. */
.hmt-avatar{width:100%;height:100%;border-radius:50%;display:inline-flex;
  align-items:center;justify-content:center;font-size:12px;font-weight:700;color:var(--c-white);}

.hmt-actions{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;
  margin-inline:16px;}
.hmt-action{display:flex;align-items:center;gap:12px;padding:16px;border-radius:22px;
  width:100%;box-sizing:border-box;text-align:start;font-family:inherit;
  transition:transform .12s ease;}
.hmt-action:active{transform:scale(.96);}
.hmt-action-ic{width:40px;height:40px;flex:none;border-radius:14px;
  display:inline-flex;align-items:center;justify-content:center;}
.hmt-action-label{font-size:13px;font-weight:700;color:var(--c-ink);letter-spacing:-.1px;}

@media (prefers-reduced-motion: reduce){
  .hmt-pulse{animation:none;}
  .hmt-action,.hmt-action:active{transition:none;transform:none;}
}
`;

// Idempotency via DOM tag, matching kitCss.js / tabCss.js exactly.
function ensureHomeCss() {
    if (typeof document === 'undefined') return;
    if (document.getElementById('home-tab-css')) return;
    const tag = document.createElement('style');
    tag.id = 'home-tab-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
}

/* Station accent map — same assignments as the web OverviewPage, expressed
   as theme tokens so both themes hold. Kept as raw CSS custom properties
   (not run through the kit's toneVar) because Cashier's brand accent here
   (var(--c-green), the rebrand-blue token) does not equal the kit's 'green'
   tone (var(--c-success), the real green) — routing it through toneVar
   would silently recolour that one station. */
const STATION_VARS = {
    Kitchen: 'var(--c-amber)',
    Bar: 'var(--c-blue)',
    Cashier: 'var(--c-green)',
    FOH: 'var(--c-purple)',
    'Front of House': 'var(--c-purple)',
};
const DEFAULT_STATION_VAR = 'var(--c-teal)';

const fmt = (v, currency) => {
    if (v == null) return currency + '0';
    return currency + Number(v).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
};

const ICON = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
};

/* ---- quick-action tile icon set — local to Home, matching Operations/
   Team's precedent of a card-specific icon set kept local to the screen
   that consumes it rather than growing the shared kit set for four
   consumers. Inline SVG only, never emoji. ---- */
const IconNewExpense = () => (
    <svg {...ICON} strokeWidth={2.2}><path d="M12 5v14M5 12h14" /></svg>
);
const IconViewOrders = () => (
    <svg {...ICON} strokeWidth={2}>
        <path d="M8 6h10M8 12h10M8 18h10" />
        <circle cx="4" cy="6" r="1" fill="currentColor" stroke="none" />
        <circle cx="4" cy="12" r="1" fill="currentColor" stroke="none" />
        <circle cx="4" cy="18" r="1" fill="currentColor" stroke="none" />
    </svg>
);
const IconStaffToday = () => (
    <svg {...ICON} strokeWidth={2}>
        <circle cx="9" cy="8" r="3.2" />
        <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
        <path d="M15.5 5.4a3.2 3.2 0 0 1 0 5.2M17.6 14a5.5 5.5 0 0 1 3 5" />
    </svg>
);
const IconEndOfDay = () => (
    <svg {...ICON} strokeWidth={2}>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5V12l3.2 2" />
    </svg>
);

// id must match the useHubCards card id it gates on — expenses/transactions/
// eod live under the Operations hub, schedule under Team (brief item 6): a
// tile renders only when that card's state is 'active', never as a dead
// navigation-only tile (spec §5 P7 — the reason the old hub grids were
// removed). Action tiles DO something (push straight into a task), unlike a
// hub card which only moves you — that distinction is why Home alone keeps
// a 2x2 grid.
const ACTION_TILES = [
    {
        id: 'expenses', labelKey: 'quick_action_new_expense', fallback: 'New Expense', Icon: IconNewExpense, tone: 'blue',
    },
    {
        id: 'transactions', labelKey: 'quick_action_view_orders', fallback: 'View Orders', Icon: IconViewOrders, tone: 'teal',
    },
    {
        id: 'schedule', labelKey: 'quick_action_staff_today', fallback: 'Staff Today', Icon: IconStaffToday, tone: 'purple',
    },
    {
        id: 'eod', labelKey: 'quick_action_end_of_day', fallback: 'End of Day', Icon: IconEndOfDay, tone: 'amber',
    },
];

export default function HomeTab() {
    ensureKitCss();
    ensureTabCss();
    ensureHomeCss();

    const {
        username, getBrandName, getCurrency, hasFeature,
    } = useAuth();
    const { t } = useLanguage();
    const { push } = useNav();
    const currency = getCurrency();
    const brand = getBrandName();

    const [stats, setStats] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | error

    // Previous closed business day — same payload the web EodGlanceWidget uses.
    // Kept in its own state and its own request: it must not be able to fail
    // the whole screen, and dashboard-stats has nothing to do with it.
    const [eodReport, setEodReport] = useState(null);
    const [eodLoading, setEodLoading] = useState(true);

    // Same data source as the web OverviewPage — dashboardService.getStats()
    // (GET /portal/dashboard-stats), reused unchanged.
    const load = useCallback(() => {
        setStatus('loading');
        dashboardService.getStats()
            .then((data) => { setStats(data); setStatus('ready'); })
            .catch((err) => {
                console.error('Dashboard stats error:', err);
                setStatus('error');
            });
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        let cancelled = false;
        setEodLoading(true);
        eodReportService.getLastClosedReport()
            .then((data) => { if (!cancelled) setEodReport(data); })
            .catch((err) => {
                // A manager-only endpoint on a screen any role can open, so a
                // 403 here is expected, not exceptional — render nothing.
                console.error('Last closed business day error:', err);
                if (!cancelled) setEodReport(null);
            })
            .finally(() => { if (!cancelled) setEodLoading(false); });
        return () => { cancelled = true; };
    }, []);

    // Quick-action / attention-row gating spans THREE hubs: expenses/
    // transactions/eod live under Operations, schedule under Team, and the
    // low-stock row's destination (inventory) lives under More. One merged
    // id -> state lookup keeps every gating check below a simple
    // `cardStates[id] === 'active'` regardless of which hub owns the card.
    const opsCards = useHubCards('operations');
    const teamCards = useHubCards('team');
    const moreCards = useHubCards('more');
    const cardStates = useMemo(() => {
        const map = {};
        [...opsCards, ...teamCards, ...moreCards].forEach((c) => { map[c.id] = c.state; });
        return map;
    }, [opsCards, teamCards, moreCards]);

    const greeting = useMemo(() => {
        const h = new Date().getHours();
        if (h < 12) return t('good_morning', {}, 'Good morning');
        if (h < 17) return t('good_afternoon', {}, 'Good afternoon');
        return t('good_evening', {}, 'Good evening');
    }, [t]);

    const kpis = stats?.kpis;
    const trend = stats?.revenue_trend || [];
    const shifts = stats?.shifts || [];
    const lowStock = stats?.low_stock_items || [];

    const isEmpty = status === 'ready' && !(
        (kpis?.revenue?.value || 0) > 0
        || (kpis?.orders?.value || 0) > 0
        || trend.some((d) => (d.revenue || 0) > 0)
        || shifts.length > 0
    );

    /* ---- header (always shown — greeting never skeletons). Preserved
       verbatim: greeting text, username/brand fallback, "Open" live pill. */
    const header = (
        <div className="hmt-greet">
            <h1 className="hmt-hello">{greeting}, {username || brand}</h1>
            <div className="hmt-biz">
                <span>{brand}</span>
                {stats?.has_active_session && (
                    <span className="hmt-live">
                        <span className="hmt-pulse" aria-hidden="true" />
                        {t('open', {}, 'Open')}
                    </span>
                )}
            </div>
        </div>
    );

    /* ---- loading ---- */
    if (status === 'loading') {
        return (
            <div className="tab-wrap">
                {header}
                <div data-testid="home-skeleton" aria-hidden="true">
                    <div className="tab-skel" style={{ height: 160 }} />
                    <div style={{ display: 'flex', gap: 10, margin: '0 16px 12px' }}>
                        <div className="tab-skel" style={{ height: 92, flex: 1, margin: 0 }} />
                        <div className="tab-skel" style={{ height: 92, flex: 1, margin: 0 }} />
                        <div className="tab-skel" style={{ height: 92, flex: 1, margin: 0 }} />
                    </div>
                    <div className="tab-skel" style={{ height: 190 }} />
                </div>
            </div>
        );
    }

    /* ---- error + retry ---- */
    if (status === 'error') {
        return (
            <div className="tab-wrap">
                {header}
                <div className="tab-state" data-testid="home-error">
                    <p className="tab-empty-title">{t('home_error_title', {}, "Couldn't load your overview")}</p>
                    <p className="tab-empty-msg">
                        {t('home_error_msg', {}, 'Check your connection and try again.')}
                    </p>
                    <button type="button" className="tab-retry" onClick={load}>
                        {t('retry', {}, 'Retry')}
                    </button>
                </div>
            </div>
        );
    }

    /* ---- empty day ---- */
    if (isEmpty) {
        return (
            <div className="tab-wrap">
                {header}
                <div className="tab-state" data-testid="home-empty">
                    <p className="tab-empty-title">{t('home_empty_title', {}, 'No sales yet')}</p>
                    <p className="tab-empty-msg">
                        {t('home_empty_msg', {}, 'Sales and team activity will appear here once your first orders come in.')}
                    </p>
                </div>
            </div>
        );
    }

    /* ---- ready ---- */
    const revenue = kpis?.revenue || {};
    const orders = kpis?.orders || {};
    const onShift = kpis?.on_shift || {};
    const sessions = stats?.sessions;

    const revenueValue = Number(revenue.value || 0);
    const revenueNeg = revenueValue < 0;
    const heroValue = `${revenueNeg ? '-' : ''}${fmt(Math.abs(revenueValue), currency)}`;
    const changePct = Number(revenue.change_pct || 0);
    const deltaValue = `${Math.abs(changePct).toFixed(1)}%`;
    // Finding 3 (final review, spec §12a): the delta pill's up/down icon is
    // aria-hidden, so without this a screen reader hears the percentage with
    // no direction at all. Composed from Task 8's `up`/`down` keys — Home is
    // the kit's first delta consumer, so this is also HeroStat's first real
    // deltaLabel usage.
    const deltaDir = changePct >= 0 ? t('up', {}, 'up') : t('down', {}, 'down');
    const deltaLabel = `${deltaDir} ${deltaValue}`;

    // Bug fix (brief item 3): kpis.on_shift.clocked_in counts every Shift
    // row tenant-wide with status "In Progress" — it is NOT scoped to
    // today's sales_date (see backend/app/routers/portal_dashboard.py
    // _build_kpis), so on a slow day it can silently show yesterday's staff
    // as still clocked in. shifts[] IS date-scoped (built from
    // Shift.date == sales_date), so deriving the count from it is the only
    // correct source.
    const clockedInNow = shifts.filter((s) => s.is_clocked_in).length;

    const attentionItems = [];
    if (hasFeature('inventory') && lowStock.length > 0) {
        const names = lowStock.slice(0, 2).map((i) => i.name).join(', ');
        // Tone reflects the WORST severity across the whole list, not
        // lowStock[0]'s. The backend orders low_stock_items by ascending
        // absolute current_stock, not by severity ratio (current_stock /
        // minimum_stock_level) — a `warning` item with a tiny absolute count
        // can sort ahead of a `critical` item with a larger one, so reading
        // tone off the first entry can understate a real critical shortage
        // elsewhere in the same list.
        const hasCritical = lowStock.some((i) => i.severity === 'critical');
        attentionItems.push({
            id: 'low-stock',
            tone: hasCritical ? 'red' : 'amber',
            text: t(
                'low_stock_alert_text',
                { count: lowStock.length, names },
                '{count} low stock item(s): {names}',
            ),
            onPress: cardStates.inventory === 'active' ? () => push('inventory') : undefined,
        });
    }
    if (sessions?.has_discrepancy) {
        attentionItems.push({
            id: 'till-discrepancy',
            tone: 'amber',
            text: t('till_discrepancy_text', {}, 'A till session has a cash discrepancy'),
            onPress: cardStates.eod === 'active' ? () => push('eod') : undefined,
        });
    }

    const activeTiles = ACTION_TILES.filter((tile) => cardStates[tile.id] === 'active');

    return (
        <div className="tab-wrap">
            {header}

            {/* Hero revenue — HeroStat's sparkline replaces the old
                hand-rolled sparkPaths() SVG entirely. */}
            <GlassCard veil className="hmt-hero-card" data-testid="hero-revenue">
                <HeroStat
                    label={t('todays_revenue', {}, "Today's Revenue")}
                    value={heroValue}
                    delta={{
                        value: deltaValue,
                        direction: changePct >= 0 ? 'up' : 'down',
                    }}
                    deltaLabel={deltaLabel}
                    caption={t(
                        'vs_amount_yesterday',
                        { amount: fmt(revenue.previous, currency) },
                        'vs {amount} yesterday',
                    )}
                    points={trend.map((d) => Number(d.revenue) || 0)}
                    sparkAriaLabel={t('sales_last_7_days', {}, 'Sales · Last 7 Days')}
                />
            </GlassCard>

            {/* Mini KPI row */}
            <div className="hmt-kpis">
                <GlassCard veil className="hmt-kpi">
                    <span className="hmt-kpi-ic">
                        <svg {...ICON}><path d="M9 11V7a3 3 0 0 1 6 0v4" /><path d="M5 9h14l1 12H4z" /></svg>
                    </span>
                    <div className="hmt-kpi-v pk-num">{Number(orders.value || 0).toLocaleString()}</div>
                    <div className="hmt-kpi-k">{t('orders_today', {}, 'Orders')}</div>
                </GlassCard>
                <GlassCard veil className="hmt-kpi">
                    <span className="hmt-kpi-ic">
                        <svg {...ICON}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                    </span>
                    <div className="hmt-kpi-v pk-num">{fmt(orders.aov, currency)}</div>
                    <div className="hmt-kpi-k">{t('avg_order_value', {}, 'Avg Order')}</div>
                </GlassCard>
                <GlassCard veil className="hmt-kpi">
                    <span className="hmt-kpi-ic hmt-amber">
                        <svg {...ICON}>
                            <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
                            <circle cx="9.5" cy="7" r="4" />
                            <path d="M21 21v-2a4 4 0 0 0-3-3.85" />
                            <path d="M15 3.15a4 4 0 0 1 0 7.7" />
                        </svg>
                    </span>
                    <div className="hmt-kpi-v pk-num" data-testid="on-shift-count">
                        {clockedInNow}/{onShift.scheduled || 0}
                    </div>
                    <div className="hmt-kpi-k">{t('kpi_on_shift', {}, 'On Shift')}</div>
                </GlassCard>
            </div>

            {/* Surfaced exceptions — low stock + till discrepancy. Fetched
                by this screen since day one but never rendered until now. */}
            <AttentionList items={attentionItems} />

            {/* Previous business day — what sold, and what it took from stock.
                Gated on the SAME hub card as the End of Day quick-action tile,
                so a role that cannot open the EOD screen is never shown a
                summary of it (the endpoint is manager-only regardless). */}
            {cardStates.eod === 'active' && (
                <HomeEodGlance
                    report={eodReport}
                    loading={eodLoading}
                    currency={currency}
                    onOpenEod={() => push('eod')}
                />
            )}

            {/* On shift today */}
            {hasFeature('hr') && (
                <>
                    <div className="hmt-sechead">
                        <span className="pk-label">{t('on_shift_today', {}, 'On Shift Today')}</span>
                        {shifts.length > 0 && (
                            <span className="pk-label pk-num">
                                {onShift.scheduled || shifts.length} {t('scheduled', {}, 'scheduled')}
                            </span>
                        )}
                    </div>
                    {shifts.length === 0 ? (
                        <div className="tab-group">
                            <GlassCard veil className="tab-state">
                                <p className="tab-empty-msg">{t('no_shifts_scheduled', {}, 'No shifts scheduled today')}</p>
                            </GlassCard>
                        </div>
                    ) : (
                        <div className="tab-group" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {shifts.map((s, i) => {
                                const accent = STATION_VARS[s.station] || DEFAULT_STATION_VAR;
                                return (
                                    <CondensedRow
                                        key={`${s.employee_name}-${i}`}
                                        icon={(
                                            // data-accent is a test-only observability hook: jsdom's
                                            // CSSOM (cssstyle) silently drops var()-valued inline
                                            // styles, so the accent can't be asserted via
                                            // style/getAttribute('style') in tests. Harmless in
                                            // production — the actual colour is the inline
                                            // `style.background` below.
                                            <span className="hmt-avatar" data-accent={accent} style={{ background: accent }}>
                                                {s.employee_initials}
                                            </span>
                                        )}
                                        tone="muted"
                                        name={s.employee_name}
                                        secondary={`${s.station} · ${s.start_time}–${s.end_time}`}
                                        pill={{
                                            label: s.is_clocked_in
                                                ? t('clocked_in', {}, 'Clocked in')
                                                : t('not_yet', {}, 'Not yet'),
                                            tone: s.is_clocked_in ? 'green' : 'muted',
                                        }}
                                    />
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {/* Quick actions — the one 2x2 grid the redesign keeps, because
                every tile here performs an action rather than just
                navigating (spec §5 P7). A tile is omitted, not disabled,
                when its hub card isn't active — never a dead tile. */}
            {activeTiles.length > 0 && (
                <>
                    <div className="pk-section-label">{t('quick_actions', {}, 'Quick Actions')}</div>
                    <div className="hmt-actions">
                        {activeTiles.map((tile) => {
                            const { Icon } = tile;
                            const token = toneVar(tile.tone);
                            return (
                                <GlassCard
                                    key={tile.id}
                                    veil
                                    lift
                                    as="button"
                                    type="button"
                                    className="hmt-action"
                                    data-testid={`quick-action-${tile.id}`}
                                    onClick={() => push(tile.id)}
                                >
                                    <span
                                        className="hmt-action-ic"
                                        style={{
                                            background: `color-mix(in srgb, var(${token}) 12%, transparent)`,
                                            color: `var(${token})`,
                                        }}
                                    >
                                        <Icon />
                                    </span>
                                    <span className="hmt-action-label">{t(tile.labelKey, {}, tile.fallback)}</span>
                                </GlassCard>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
